function parseOptionalDate(value) {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function sanitizeUrl(value) {
  if (!value) {
    return "";
  }

  const trimmed = value.trim();

  if (!trimmed) {
    return "";
  }

  if (trimmed.indexOf("/") === 0 || trimmed.indexOf("#") === 0) {
    return trimmed;
  }

  const anchor = document.createElement("a");
  anchor.href = trimmed;

  if (
    anchor.protocol === "http:" ||
    anchor.protocol === "https:" ||
    anchor.protocol === "mailto:" ||
    anchor.protocol === "tel:"
  ) {
    return trimmed;
  }

  return "";
}

function getCurrentPathname() {
  if (typeof window === "undefined" || !window.location) {
    return "";
  }

  return window.location.pathname || "";
}

function normalizePathname(value) {
  if (!value) {
    return "";
  }

  const trimmed = value.trim();

  if (!trimmed) {
    return "";
  }

  try {
    return new URL(trimmed, window.location.origin).pathname;
  } catch (error) {
    return trimmed.split(/[?#]/)[0];
  }
}

export function isNoticeModalExcludedPath(pathname) {
  const normalizedPathname = normalizePathname(pathname);

  return (
    /^\/Consulta\/[^/]+\/NovoParticipante\/?$/.test(normalizedPathname) ||
    /^\/Consulta\/[^/]+\/EsqueciSenha\/?$/.test(normalizedPathname) ||
    /^\/Confirma\/[^/]+\/?$/.test(normalizedPathname)
  );
}

function sanitizeNode(node, documentRef) {
  const allowedTags = {
    A: ["href", "target", "title", "rel"],
    B: [],
    BR: [],
    EM: [],
    I: [],
    LI: [],
    OL: [],
    P: [],
    SPAN: [],
    STRONG: [],
    U: [],
    UL: []
  };
  const blockedTags = {
    IFRAME: true,
    LINK: true,
    META: true,
    OBJECT: true,
    SCRIPT: true,
    STYLE: true
  };

  if (node.nodeType === 3) {
    return documentRef.createTextNode(node.textContent);
  }

  if (node.nodeType !== 1) {
    return null;
  }

  const tagName = node.tagName.toUpperCase();

  if (blockedTags[tagName]) {
    return null;
  }

  if (!allowedTags[tagName]) {
    const fragment = documentRef.createDocumentFragment();

    Array.prototype.forEach.call(node.childNodes, (childNode) => {
      const sanitizedChild = sanitizeNode(childNode, documentRef);

      if (sanitizedChild) {
        fragment.appendChild(sanitizedChild);
      }
    });

    return fragment;
  }

  const sanitizedElement = documentRef.createElement(tagName.toLowerCase());

  Array.prototype.forEach.call(allowedTags[tagName], (attributeName) => {
    const attributeValue = node.getAttribute(attributeName);

    if (!attributeValue) {
      return;
    }

    if (tagName === "A" && attributeName === "href") {
      const sanitizedHref = sanitizeUrl(attributeValue);

      if (sanitizedHref) {
        sanitizedElement.setAttribute("href", sanitizedHref);
      }

      return;
    }

    if (tagName === "A" && attributeName === "target") {
      const targetValue =
        attributeValue === "_self" ||
        attributeValue === "_parent" ||
        attributeValue === "_top"
          ? attributeValue
          : "_blank";

      sanitizedElement.setAttribute("target", targetValue);

      if (targetValue === "_blank") {
        sanitizedElement.setAttribute("rel", "noopener noreferrer");
      }

      return;
    }

    if (tagName === "A" && attributeName === "rel") {
      return;
    }

    sanitizedElement.setAttribute(attributeName, attributeValue);
  });

  Array.prototype.forEach.call(node.childNodes, (childNode) => {
    const sanitizedChild = sanitizeNode(childNode, documentRef);

    if (sanitizedChild) {
      sanitizedElement.appendChild(sanitizedChild);
    }
  });

  return sanitizedElement;
}

export function sanitizeNoticeHtml(html) {
  if (!html) {
    return "";
  }

  const unsafeDocument = document.implementation.createHTMLDocument("");
  const safeDocument = document.implementation.createHTMLDocument("");
  const unsafeContainer = unsafeDocument.createElement("div");
  const safeContainer = safeDocument.createElement("div");

  unsafeContainer.innerHTML = html;

  Array.prototype.forEach.call(unsafeContainer.childNodes, (childNode) => {
    const sanitizedChild = sanitizeNode(childNode, safeDocument);

    if (sanitizedChild) {
      safeContainer.appendChild(sanitizedChild);
    }
  });

  return safeContainer.innerHTML;
}

export function shouldShowNoticeModal(
  config,
  pathname = getCurrentPathname()
) {
  if (!config || !config.enabled) {
    return false;
  }

  if (isNoticeModalExcludedPath(pathname)) {
    return false;
  }

  const startAt = parseOptionalDate(config.startAt);
  const endAt = parseOptionalDate(config.endAt);
  const now = new Date();
  const button = config.button || {};

  if (config.startAt && !startAt) {
    return false;
  }

  if (config.endAt && !endAt) {
    return false;
  }

  if (startAt && now < startAt) {
    return false;
  }

  if (endAt && now > endAt) {
    return false;
  }

  return Boolean(
    config.title &&
      config.content &&
      button.label &&
      button.url &&
      sanitizeUrl(button.url)
  );
}
